import { DateTime } from 'luxon';
import { Filter } from './Filter';
import { AccountParams } from './AccountParams';

// Cloud API responses kept in memory for the session, by request
const cache = new Map<string, { expiresAt: number, value: Promise<any> }>();

/**
 * Returns the cached response of a request, or makes it and keeps it for ttl ms. Concurrent
 * callers share the same request. Failures are not kept. Every caller gets its own copy.
 */
const cachedRequest = <T>(key: string, ttl: number, request: () => Promise<T>): Promise<T> => {
  const now = Date.now();
  let entry = cache.get(key);
  if(!entry || entry.expiresAt <= now) {
    const value = request();
    const newEntry = { expiresAt: now + ttl, value };
    cache.set(key, newEntry);
    value.catch(() => {
      cache.get(key) === newEntry && cache.delete(key);
    });
    entry = newEntry;
  }
  return entry.value.then(value => structuredClone(value));
}

/**
 * Key of a Cloud API request: the filter fields the Cloud API sends (dates by day, as it
 * queries whole days), the account and any other argument.
 */
const requestKey = (name: string, filter: Filter, params: AccountParams, ...args: any[]): string => {
  const day = (date?: Date) => date ? DateTime.fromJSDate(date).toISODate() : null;
  return JSON.stringify([
    name, params.host, params.token || params.id,
    filter.locationId?.length ? filter.locationId.join('|') : null,
    filter.installationId || null, filter.campaignId || null, filter.playlistId || null,
    filter.zone || null, filter.gate || null, filter.crossingLine || null,
    day(filter.from), day(filter.to),
    ...args
  ]);
}

const clearRequestCache = () => cache.clear();

export { cachedRequest, requestKey, clearRequestCache };
